import Image from 'next/image';
import { useCallback, useRef, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useRouter } from 'next/navigation';

import { getClient, searchClient } from '@/services/client.service';
import { debounce } from '@/utils/debounce';
import getCountry from '@/utils/get-country';
import { getOnboardingURL } from '@/utils/onboarding-urls';
import {
  ACTIONS,
  BUTTON_TYPES,
  trackButtonClicked,
  trackClientEligibilityChecked,
} from '@/utils/track';

const SUBMIT_LABEL = 'Get Started';

import { baseStyles } from '../../core/CoverageForm/CompaniesDropdown/dropdown.styles';
import { Dropdown } from '../../core/CoverageForm/Dropdown';
import { CompaniesDropdown } from '../../core/CoverageForm/CompaniesDropdown';
import { products } from '@/utils/products';
import { Content, Wrapper, Form, Field, Title, CloseButtonPopUp } from '../styles';
import { Heading, Paragraph, RichText } from '@swordhealth/ui-corporate';
import { MarkdownRichText } from '../../shared';
import { compareDates } from '@/utils/dates';
import { StyledButton } from './styles';

const ClientSelect = ({
  content,
  solution,
  setClientKey,
  setCountry,
  setClientName,
  params,
  conditionLabel,
  onClose,
  skipProgram = false,
  isExitPopup,
}) => {
  const {
    search_title,
    searchTitles,
    search_description,
    search_placeholder,
    search_cta,
    search_description_title,
  } = content;
  const [isNotCovered, setIsNotCovered] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [countries, setCountries] = useState(null);
  const [focusSelect, setFocusSelect] = useState(false);
  const solutionTitle = searchTitles.find((titles) => titles.solution === solution);

  const selectClient = useRef(null);
  const router = useRouter();

  useEffect(() => {
    if (focusSelect) {
      selectClient?.current?.focus();
      setFocusSelect(false);
    }
  }, [focusSelect]);

  const formik = useFormik({
    initialValues: {
      client: null,
      country: null,
    },
    onSubmit: async (values, actions) => {
      setLoading(true);
      if (!values.client || (countries && !values.country)) {
        selectClient?.current?.focus();
        setLoading(false);
        return;
      }

      if (values.client && !isNotCovered) {
        let fetchedClient = values.client;

        if (!fetchedClient.clientKey) {
          fetchedClient = await getClient({
            client: values.client.key,
            productKey: solution.toLowerCase(),
          });
          if (setClientName) {
            setClientName(fetchedClient?.name);
          }
        }

        if (fetchedClient && solution === 'platform' && !skipProgram) {
          setClientKey(values.client?.key);
          setCountry(values?.country || null);
          setLoading(false);
          return;
        }

        if (fetchedClient && compareDates(fetchedClient?.launchDate)) {
          return router.push(`/waiting-list?client=${fetchedClient.clientKey}`);
        }

        const url = getOnboardingURL({
          client: fetchedClient,
          country: values?.country,
          programType: products[solution.toUpperCase()].param,
          query: Object.fromEntries(params),
        });

        trackClientEligibilityChecked({
          clientExists: true,
          clientCountry: values?.country?.key,
          clientId: fetchedClient.clientKey,
          clientName: fetchedClient.name,
        });

        trackButtonClicked({
          action: ACTIONS.REDIRECT_TO_ONBOARDING,
          buttonType: BUTTON_TYPES.NAVIGATION,
          buttonText: search_cta?.button?.label ?? SUBMIT_LABEL,
          destination: url,
          buttonLocation: 'form',
        });

        actions.resetForm({
          values: {
            client: null,
            country: null,
          },
        });

        setCountries(null);
        window.location.assign(url);
        setLoading(false);
        return;
      }
      trackClientEligibilityChecked({
        clientExists: false,
        clientCountry: null,
        clientId: values.client.key,
        clientName: values.client.label,
      });

      setIsNotCovered(true);

      actions.resetForm({
        values: {
          client: null,
          country: null,
        },
      });

      router.push(`/not-covered?client=${values.client.key}&program=${solution}`);
      setLoading(false);
    },
  });

  const validateClient = useCallback(
    async (selectedClient, formik) => {
      formik.setStatus({ validating: true });
      setCountries(null);
      try {
        const fetchedClient = await getClient({
          client: selectedClient.key,
          productKey: products[solution.toUpperCase()].key,
        });
        if (fetchedClient?.countries?.length > 1) {
          setCountries(fetchedClient.countries);
        }
      } catch {
        setIsNotCovered(true);
      } finally {
        formik.setStatus({ validating: false });
      }
    },
    [solution],
  );

  const loadOptions = useCallback(
    (query, callback) => {
      searchClient({ query, ...products[solution.toUpperCase()] }).then((results) => {
        if (results.length === 0) {
          callback([{ key: query, label: query }]);
        } else {
          callback(
            results.map((result) => {
              return {
                key: result.clientKey || '',
                label: result.name,
              };
            }),
          );
        }
      });
    },
    [solution],
  );

  const handleReset = () => {
    formik.resetForm();
    setCountries(null);
  };

  return (
    <Content $isFullHeight={isExitPopup}>
      <Heading as="h1" size="3xl" id="client-select-title">
        <RichText>
          {!searchTitles || !solutionTitle ? (
            <Title elements={['strong']}>{search_title}</Title>
          ) : !conditionLabel || conditionLabel.toLowerCase() === 'other' ? (
            <Title elements={['strong']}>{solutionTitle?.title}</Title>
          ) : (
            <Title elements={['strong']}>
              {solutionTitle?.titleCondition?.replace('{{condition}}', conditionLabel)}
            </Title>
          )}
        </RichText>
      </Heading>
      <Wrapper>
        {onClose && (
          <CloseButtonPopUp onClick={onClose}>
            <Image src="/icons/ic_close.svg" width={20} height={20} alt="close" />
          </CloseButtonPopUp>
        )}
        {search_description_title && (
          <RichText>
            <Heading>{search_description_title}</Heading>
          </RichText>
        )}
        {search_description && (
          <Paragraph size="sm" id="client-select-description">
            <MarkdownRichText elements={['a', 'strong']}>{search_description}</MarkdownRichText>
          </Paragraph>
        )}
        <Form
          data-testid="onboarding-form"
          onSubmit={formik.handleSubmit}
          $error={formik.errors.client && formik.touched.client}
        >
          <Field>
            <CompaniesDropdown
              innerRef={selectClient}
              name="companies"
              customBaseStyles={baseStyles}
              className="companiesDropdown"
              placeholder={search_placeholder ?? 'Enter your employer or health plan'}
              loadOptions={debounce(loadOptions)}
              noOptionsMessage={() => null}
              value={formik.values.client}
              onChange={(value) => {
                formik.setValues({ client: value, country: null });
                validateClient(value, formik);
              }}
              error={formik.errors.client && formik.touched.client}
              errorLabel={'Not covered'}
              onFocus={() => {
                if (formik.values.client !== null) {
                  setCountries(null);
                  formik.setValues({ client: null, country: null });
                }
              }}
              handleReset={handleReset}
            />
          </Field>

          {countries && (
            <Field>
              <Dropdown
                name="country"
                className="countryDropdown"
                placeholder="Select your region/country"
                options={countries.map((country) => getCountry(country.country))}
                noOptionsMessage={() => 'No results available'}
                value={formik.values.country}
                onChange={(value) => {
                  formik.setFieldValue('country', value);
                }}
                customBaseStyles={baseStyles}
              />
            </Field>
          )}
          <StyledButton
            $isFullWidth={skipProgram}
            type="submit"
            loading={formik.status?.validating || isLoading}
            disabled={formik.status?.validating}
            className="submitButton"
            id="eligibility-check-button"
          >
            {search_cta?.button?.label ?? SUBMIT_LABEL}
          </StyledButton>
        </Form>
      </Wrapper>
    </Content>
  );
};

export default ClientSelect;
